import type { Action, Store } from 'redux'

import { FlowPage } from '@monthio/design-system'
import {
	eventHubService,
	flowAnimationService,
	LoadingScreen,
	Logger,
	trackingService,
} from '@monthio/react-components'
import isWebview from 'is-ua-webview'
import Cookies from 'js-cookie'
import { useEffect } from 'react'
import { Provider, useSelector } from 'react-redux'
import {
	Redirect,
	Route,
	Switch,
	useHistory,
	useLocation,
} from 'react-router-dom'

import type { RootState } from './Redux/reducers'
import type { ConsumerAuthenticationState } from './Redux/reducers/consumerDataPullSessionReducers/consumerAuthenticationReducer'
import type { ConsumerSessionDataState } from './Redux/reducers/consumerDataPullSessionReducers/consumerSessionDataReducer'

import { transactionApiClient } from '../../api/transactionApiClient'
import { routeCodes } from './Bank.types'
import { BankLoginPage } from './Components/BankLoginPage'
import { BankSelectorPage } from './Components/BankSelector'
import { BankSessionOverviewPage } from './Components/BankSessionOverviewPage'
import { ReopenSession } from './Components/ReopenSession'
import { getAllBankSessions } from './Redux/actions/consumerDataPullSessionActions/bankSessionActions'
import { authenticateWithSessionId } from './Redux/actions/consumerDataPullSessionActions/consumerAuthenticationActions'
import { reopenConsumerSession } from './Redux/actions/consumerDataPullSessionActions/consumerSessionDataActions'
import configureStore from './Redux/configureStore'
import { ConsumerAuthenticationStatus } from './Redux/reducers/consumerDataPullSessionReducers/consumerAuthenticationReducer'
import { showAnimationSelector } from './Redux/showAnimationSelector'
import { useBankModule } from './useBankModule'

let store:
	| (Store<RootState, Action<any>> & {
			dispatch: unknown
	  })
	| null = null

export interface ForwardedProperties {
	sessionId: string
	hasExplicitConsent: boolean
	clientName: string
	calculationReferenceDate?: Date
}

export const monthioInternalBankIdCookieName = 'monthio_bank_id'
export const authUrlCookieName = 'monthio_auth_url'

export const logger = new Logger('TransactionClientMicrofrontend')

export const Bank = () => {
	const { config } = useBankModule()
	const history = useHistory()
	const { forwardedProperties } = config

	if (store == null) {
		store = configureStore()
	}

	useEffect(() => {
		transactionApiClient.initialize()
		if (store == null) {
			store = configureStore()
		}
		if (history.location.pathname === '/banks/reopen-session') {
			store.dispatch(reopenConsumerSession(forwardedProperties.sessionId))
			store.dispatch(getAllBankSessions())
			logger.info('reopeningSession started')
			trackingService.trackTechnicalEvent('reopeningSessionStarted')
			return
		}
		store.dispatch(authenticateWithSessionId(forwardedProperties.sessionId))
	}, [])

	return (
		<Provider store={store}>
			<App forwardedProps={forwardedProperties} />
		</Provider>
	)
}

const App = ({ forwardedProps }: { forwardedProps: ForwardedProperties }) => {
	const location = useLocation()
	const history = useHistory<{
		authUrl?: string
		consentCode?: string
		bankId?: string
		monthioInternalBankId?: number
	}>()

	const authState = useSelector<RootState, ConsumerAuthenticationState>(
		(state) => state.consumerAuthentication,
	)

	const sessionData = useSelector<RootState, ConsumerSessionDataState>(
		(state) => state.consumerSessionData,
	)

	const { hasExplicitConsent, clientName, calculationReferenceDate } =
		forwardedProps

	const showAnimation = useSelector<RootState, boolean | undefined>(
		showAnimationSelector,
	)

	useEffect(() => {
		if (showAnimation !== undefined) {
			flowAnimationService.showAnimation = showAnimation
		}
	}, [showAnimation])

	useEffect(() => {
		if (
			!sessionData.reopeningSession &&
			!sessionData.reopeningSessionSuccessful &&
			authState.applicantSessionFinishedOn
		) {
			eventHubService.onEvent({
				source: 'transactionClient',
				topic: 'moduleCompleted',
				payload: { module: 'bank', skipped: true },
			})
		}
	}, [
		authState.applicantSessionFinishedOn,
		sessionData.reopeningSession,
		sessionData.reopeningSessionSuccessful,
	])

	useEffect(() => {
		const redirectInWebview = () => {
			const params = new URLSearchParams(window.location.search)
			const consentCode = params.has('consentCode')
			if (consentCode) {
				history.replace(routeCodes.bankSessionOverviewPage, {
					authUrl: Cookies.get(authUrlCookieName),
					consentCode: params.get('consentCode') || '', // skipcq: JS-W1043
					monthioInternalBankId: Cookies.get(monthioInternalBankIdCookieName)
						? Number(Cookies.get(monthioInternalBankIdCookieName))
						: undefined,
				})
			}
		}

		if (isWebview(window.navigator.userAgent)) {
			redirectInWebview()
		}
	}, [])

	if (authState.status !== ConsumerAuthenticationStatus.AUTHENTICATE_SUCCESS) {
		return (
			<FlowPage>
				<FlowPage.Content $isCentered>
					<LoadingScreen />
				</FlowPage.Content>
			</FlowPage>
		)
	}

	return (
		<Switch location={location}>
			<Route
				path={routeCodes.banks}
				render={() => <BankSelectorPage history={history} />}
			/>
			<Route path="/banks/reopen-session">
				<ReopenSession />
			</Route>
			<Route
				path={routeCodes.bankLoginPage}
				render={() => (
					<BankLoginPage
						monthioInternalBankId={
							history.location?.state?.monthioInternalBankId
						}
						bankId={history.location?.state?.bankId}
					/>
				)}
			/>
			<Route
				path={routeCodes.bankSessionOverviewPage}
				render={() => (
					<BankSessionOverviewPage
						hasExplicitConsent={hasExplicitConsent}
						clientName={clientName}
						calculationReferenceDate={calculationReferenceDate}
					/>
				)}
			/>
			<Redirect to={routeCodes.banks} />
		</Switch>
	)
}
